import hljs from "highlight.js";
import slugify from "@sindresorhus/slugify";

import { ConfigurationProvider } from "../lib/infrastructure/configuration/ConfigurationProvider";
import { PAGE_FILE_SRC_ATTRIBUTE, isPageFileUrl } from "../lib/infrastructure/files/PageFileUrl";

/**
 * Turns the HTML of `@orbitalwatch/astromark` 0.1.0 into the markup the Ark reader styles, so one stylesheet serves both
 * formats. Runs on the compiler's output before `sanitizePageHtml`; it never forks or patches the compiler.
 *
 * | Construct              | markdown-it (legacy) emits                                        | AstroMark 0.1.0 emits                        | adapter                                         |
 * | ---------------------- | ----------------------------------------------------------------- | -------------------------------------------- | ----------------------------------------------- |
 * | heading                | `<hN id tabindex="-1"><a class="header-anchor" href="#id"><span>` | `<hN id>` text                               | adds `tabindex`, the `a.header-anchor > span`   |
 * | heading id             | `@sindresorhus/slugify`, duplicates `-1`, `-2`                    | own slug (drops non-ASCII), no dedupe        | re-slugs auto ids the legacy way; `{#id}` kept  |
 * | `[[toc]]`, `${toc}`    | `<nav class="table-of-contents"><ol>…`                            | literal `<p>[[toc]]</p>`                     | replaces the paragraph with the same `nav`      |
 * | fenced code            | `<pre><code class="hljs language-x">` + hljs spans                | `<pre><code class="language-x">` plain       | highlight.js, same options as markdown-it-hljs  |
 * | captioned image        | `<figure><img tabindex="0"><figcaption>`                          | `<p><figure>…</figure></p>`                  | unwraps the paragraph, adds `tabindex`          |
 * | image alone in a para  | `<figure><img tabindex="0"><figcaption>alt</figcaption>` (also `[![a](x)](y)`) | `<p><img></p>`                  | same figure, caption from the alt text          |
 * | Ark attachment image   | `<img data-ark-src>` (no `src`)                                   | `<img src>`                                  | moves `src` to `data-ark-src` (bearer loader)   |
 * | Atlas embed            | raw `<iframe src="{atlas}/embed/{guid}">`                         | raw HTML is escaped; the AstroMark idiom is  | a paragraph holding only such a link becomes    |
 * |                        |                                                                   | a link `[Diagram]({atlas}/embed/{guid})`     | `div.atlas-embed > a.atlas-embed__source + iframe.atlas-embed__frame` (editor markup) |
 * | external link          | `<a href>`                                                        | `<a href target="_blank">`                   | unchanged (sanitizer adds `rel=noopener`)       |
 * | callout `> [!NOTE]`    | blockquote with literal `[!NOTE]`                                 | `blockquote.callout.callout-note` (tip, important, warning, caution) | unchanged: new classes for the reader CSS |
 * | footnotes `[^1]`       | literal text                                                      | `sup.footnote-ref > a`, `ol.footnotes`, `a.footnote-back` | unchanged: new classes for the reader CSS |
 * | `::: tabs`             | `div.tabs-tabs-wrapper` + buttons + `div.tabs-tab-content`        | literal paragraphs                           | none: AstroMark has no containers (report)      |
 * | `Table: caption`       | `<caption>`                                                       | literal paragraph before the table           | none (report)                                   |
 * | `data:image` source    | kept                                                              | `src=""` (`safeUrl` blocks `data:`)          | none: decision pending (OrbitalPlan/08 §2.3)    |
 */

/** The placeholders markdown-it-toc-done-right recognises (`[[toc]]`, `[toc]`, `[[_toc_]]`, `${toc}`). */
const TOC_PLACEHOLDER = /^(\$\{toc\}|\[\[?_?toc_?\]?\]|\$<toc(\{[^}]*\})>)$/i;

const GUID = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

/** The same default height the AstroMark editor gives an embed before the viewer reports its size. */
const ATLAS_EMBED_HEIGHT = "420";

/** A heading as the TOC sees it. */
export interface AdaptedHeading {
    level: number;
    label: string;
    id: string;
}

export interface AdaptedHtml {
    html: string;
    headings: AdaptedHeading[];
}

/** AstroMark 0.1.0's own heading slug (`Evaluator.generateId`), to tell an automatic id from an explicit `{#id}`. */
export function astroMarkSlug(label: string): string {
    return label
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
}

function atlasOrigins(): string[] {
    try {
        const origins: string[] = ConfigurationProvider.getConfiguration()?.Atlas?.Origins ?? [];
        return origins
            .map((origin) => {
                try {
                    return new URL(origin).origin;
                } catch {
                    return null;
                }
            })
            .filter((origin): origin is string => !!origin && origin.startsWith("https://"));
    } catch {
        return [];
    }
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** The GUID when `href` is `{atlasOrigin}/embed/{guid}` on a configured Atlas origin. */
export function matchAtlasEmbed(href: string | null, origins: string[] = atlasOrigins()): string | null {
    if (!href) return null;
    for (const origin of origins) {
        const match = new RegExp(`^${escapeRegExp(origin)}/embed/(${GUID})(?:[/?#].*)?$`, "i").exec(href);
        if (match) return match[1];
    }
    return null;
}

function headingsOf(root: ParentNode): HTMLHeadingElement[] {
    return Array.from(root.querySelectorAll<HTMLHeadingElement>("h1, h2, h3, h4, h5, h6"));
}

/** Legacy anchors: auto ids re-slugged with `@sindresorhus/slugify` and de-duplicated (`-1`, `-2`) like markdown-it-anchor. */
function adaptHeadings(root: DocumentFragment, doc: Document): AdaptedHeading[] {
    const used = new Set<string>();
    const unique = (slug: string): string => {
        let candidate = slug;
        for (let i = 1; used.has(candidate); i++) candidate = `${slug}-${i}`;
        used.add(candidate);
        return candidate;
    };

    return headingsOf(root).map((heading) => {
        const label = heading.textContent ?? "";
        const current = heading.getAttribute("id");
        const isAutomatic = current === null || current === astroMarkSlug(label.trim());
        const id = isAutomatic ? unique(slugify(label)) : unique(current!);
        heading.setAttribute("id", id);
        heading.setAttribute("tabindex", "-1");

        const anchor = doc.createElement("a");
        anchor.className = "header-anchor";
        anchor.setAttribute("href", `#${id}`);
        const span = doc.createElement("span");
        while (heading.firstChild) span.appendChild(heading.firstChild);
        anchor.appendChild(span);
        heading.appendChild(anchor);

        return { level: Number(heading.tagName.substring(1)), label, id };
    });
}

/** The nested `<ol>` markdown-it-toc-done-right renders for `[[toc]]`. */
function buildTocNav(headings: AdaptedHeading[], doc: Document): HTMLElement {
    interface Node {
        level: number;
        heading?: AdaptedHeading;
        children: Node[];
    }
    const root: Node = { level: 0, children: [] };
    const stack: Node[] = [root];
    for (const heading of headings) {
        const node: Node = { level: heading.level, heading, children: [] };
        while (stack.length > 1 && node.level <= stack[stack.length - 1].level) stack.pop();
        stack[stack.length - 1].children.push(node);
        stack.push(node);
    }

    const list = (nodes: Node[]): HTMLOListElement => {
        const ol = doc.createElement("ol");
        for (const node of nodes) {
            const li = doc.createElement("li");
            const a = doc.createElement("a");
            a.setAttribute("href", `#${node.heading!.id}`);
            a.textContent = node.heading!.label;
            li.appendChild(a);
            if (node.children.length) li.appendChild(list(node.children));
            ol.appendChild(li);
        }
        return ol;
    };

    const nav = doc.createElement("nav");
    nav.className = "table-of-contents";
    if (root.children.length) nav.appendChild(list(root.children));
    return nav;
}

function adaptTocPlaceholders(root: DocumentFragment, headings: AdaptedHeading[], doc: Document): void {
    for (const p of Array.from(root.querySelectorAll("p"))) {
        if (p.children.length === 0 && TOC_PLACEHOLDER.test((p.textContent ?? "").trim())) {
            p.replaceWith(buildTocNav(headings, doc));
        }
    }
}

/** highlight.js the way markdown-it-highlightjs runs it: named language, else auto-detect; `hljs` first in the class. */
function adaptCode(root: DocumentFragment): void {
    for (const code of Array.from(root.querySelectorAll<HTMLElement>("pre > code"))) {
        const language = Array.from(code.classList).find((c) => c.startsWith("language-"))?.substring("language-".length) ?? "";
        const source = code.textContent ?? "";
        let highlighted: string | null = null;
        try {
            highlighted = language
                ? hljs.highlight(source, { language: hljs.getLanguage(language) ? language : "plaintext", ignoreIllegals: true }).value
                : hljs.highlightAuto(source).value;
        } catch {
            highlighted = null;
        }
        if (highlighted !== null) code.innerHTML = highlighted;
        code.className = ["hljs", ...Array.from(code.classList).filter((c) => c !== "hljs")].join(" ");
    }
}

/**
 * @mdit/plugin-figure: a paragraph holding only an image (or a linked image) is a figure, captioned with the title or else
 * the alt text, and the image is focusable. AstroMark makes a figure only for a titled image, so the rest is done here.
 */
function adaptFigures(root: DocumentFragment, doc: Document): void {
    for (const p of Array.from(root.querySelectorAll("p"))) {
        if ((p.textContent ?? "").trim() !== "" || p.children.length !== 1) continue;
        const only = p.children[0];
        const img = only.tagName === "IMG" ? only : only.tagName === "A" && only.children.length === 1 && only.children[0].tagName === "IMG" ? only.children[0] : null;
        if (!img || (only.tagName === "A" && (only.textContent ?? "").trim() !== "")) continue;
        const figure = doc.createElement("figure");
        for (const attr of Array.from(p.attributes)) figure.setAttribute(attr.name, attr.value);
        figure.appendChild(only);
        const caption = doc.createElement("figcaption");
        caption.textContent = img.getAttribute("alt") ?? "";
        figure.appendChild(caption);
        p.replaceWith(figure);
    }
    for (const img of Array.from(root.querySelectorAll("figure img"))) {
        if (!img.hasAttribute("tabindex")) img.setAttribute("tabindex", "0");
    }
}

function adaptImages(root: DocumentFragment): void {
    for (const img of Array.from(root.querySelectorAll("img[src]"))) {
        const src = img.getAttribute("src");
        if (src && isPageFileUrl(src)) {
            img.removeAttribute("src");
            img.setAttribute(PAGE_FILE_SRC_ATTRIBUTE, src);
        }
    }
}

/** A paragraph whose only content is a link to `{atlas}/embed/{guid}` becomes the editor's embed widget. */
function adaptAtlasEmbeds(root: DocumentFragment, doc: Document): void {
    const origins = atlasOrigins();
    if (!origins.length) return;
    for (const p of Array.from(root.querySelectorAll("p"))) {
        const only = p.children.length === 1 ? p.children[0] : null;
        if (!only || only.tagName !== "A" || (p.textContent ?? "").trim() !== (only.textContent ?? "").trim()) continue;
        const href = only.getAttribute("href");
        const guid = matchAtlasEmbed(href, origins);
        if (!guid || !href) continue;

        const wrapper = doc.createElement("div");
        wrapper.className = "atlas-embed";
        wrapper.setAttribute("data-atlas-id", guid);
        const source = doc.createElement("a");
        source.className = "atlas-embed__source";
        source.setAttribute("href", href);
        source.setAttribute("target", "_blank");
        source.textContent = only.textContent?.trim() || href;
        const frame = doc.createElement("iframe");
        frame.className = "atlas-embed__frame";
        frame.setAttribute("src", href);
        frame.setAttribute("title", only.textContent?.trim() || "Atlas diagram");
        frame.setAttribute("loading", "lazy");
        frame.setAttribute("width", "100%");
        frame.setAttribute("height", ATLAS_EMBED_HEIGHT);
        frame.setAttribute("frameborder", "0");
        wrapper.append(source, frame);
        p.replaceWith(wrapper);
    }
}

/**
 * AstroMark wraps a captioned image in a paragraph (`<p><figure>…</figure></p>`), which an HTML parser splits into two
 * empty paragraphs around the figure. AstroMark escapes `<` in text, so these tag pairs can only come from the transpiler.
 */
function unwrapFigures(html: string): string {
    return html.replace(/<p>\s*(<figure>(?:(?!<\/figure>)[\s\S])*<\/figure>)\s*<\/p>/g, "$1");
}

/** AstroMark HTML → the reader's class vocabulary (see the table at the top of this file). */
export function adaptAstroMarkHtml(html: string, doc: Document = document): AdaptedHtml {
    const template = doc.createElement("template");
    template.innerHTML = unwrapFigures(html);
    const root = template.content;

    const headings = adaptHeadings(root, doc);
    adaptTocPlaceholders(root, headings, doc);
    adaptCode(root);
    adaptFigures(root, doc);
    adaptImages(root);
    adaptAtlasEmbeds(root, doc);

    return { html: template.innerHTML, headings };
}
